import { z } from 'zod';

import type { PeriodicGrant } from './kinds';
import { SUBSCRIPTION_STATUSES } from './subscription-status';

/**
 * Pure credit WIRE/VIEW CONTRACTS — the client-safe surface with NO date-fns /
 * `@date-fns/utc` / immer dependency. The date-math engine (`UserCreditsData`,
 * `applyCreditOperation`, `summarizeCredits`) lives in `./engine`, which DOES
 * pull date-fns; the per-kind grant policies live in `./kinds`; the DO wire
 * operation shapes live in `./ops`; client display helpers live in `./view`.
 *
 * The split matters because the web/mobile clients bundle the synced `credits`
 * entity, which only needs the types + zod schemas here — never the date math.
 * So client-reachable code (the sync entity schema, credit-display hooks) imports
 * from `@flashka/shared/credits/types` (and `@flashka/shared/credits/view`),
 * keeping `@date-fns/utc` out of the client bundle. Server/worker/CLI use the
 * curated `@flashka/shared/credits` barrel.
 */

/**
 * All credit attribution types, in CONSUMPTION PRIORITY order (highest first).
 * The union and the priority rank both derive from this single array, so adding
 * a type is a one-line change. `reward`/`manual` are always-on;
 * `daily`/`beast` are tier-gated. `referral` is LEGACY: the referral program
 * was removed 2026-09-03 and nothing grants it any more, but it stays in the
 * enum so the historical grants already in users' ledgers keep validating
 * (`creditEntrySchema`) and are consumed first like before.
 */
export const CREDITS_ATTRIBUTION_TYPES = [
  'referral',
  'reward',
  'beast',
  'daily',
  'manual',
] as const;

export type CreditsAttributionType = (typeof CREDITS_ATTRIBUTION_TYPES)[number];

/**
 * The credit KINDS — independent, non-fungible wallets. Each kind is its own
 * ledger with its own grant policy (see `./kinds`), consumed only by its own
 * feature: one quiz run, one card-generation run, one transcription, one oral
 * exam, one case study, one Professor Ka message. They never cross-spend.
 * `pageUpload` is defined but not yet enforced.
 *
 * This is a HIGHER axis than {@link CREDITS_ATTRIBUTION_TYPES} — attribution
 * types are sub-pools WITHIN one kind (priority-ordered sources of the same
 * wallet), whereas kinds are separate wallets. The same domain engine
 * (`UserCreditsData`) runs once per kind, parameterized by that kind's policy,
 * so adding a kind needs no new engine/storage/sync — just a policy.
 *
 * Adding a kind = a new union member here + one self-contained block in `./kinds`.
 */
export const CREDIT_KINDS = [
  'transcription',
  'oralExam',
  'chat',
  'caseStudy',
  'cardGeneration',
  'quiz',
  'pageUpload',
] as const;

export type CreditKind = (typeof CREDIT_KINDS)[number];

/**
 * A single credit grant — one flat record in the ledger. The attribution type
 * and expiry are FIELDS, never also map keys, so they can't desync. `expiresAt`
 * is the single source of truth for expiry.
 */
export interface CreditEntry {
  /** Which pool this grant belongs to (drives priority + tier-gating). */
  attributionType: CreditsAttributionType;
  /** Total credits granted in this entry. */
  granted: number;
  /** Credits already consumed from this entry. */
  used: number;
  /** ISO of when the grant became active. */
  grantedAt: string;
  /** ISO of when the grant expires (single source of truth for expiry). */
  expiresAt: string;
  /** ISO of the next renewal, for tier grants (daily/beast). */
  renewsAt?: string;
  /** Free-form attribution metadata (e.g. rewardType). */
  meta?: Record<string, unknown>;
}

/** The active credit ledger: a flat list of grants, stored at `credits:active`. */
export type ActiveCredits = CreditEntry[];

/**
 * A grant is spendable/countable now iff it's started and not yet expired. Pure
 * ISO-string comparison (no date-fns), so it's safe to share between `./kinds`,
 * the engine, and the client-reachable surface alike. Engine/policy plumbing,
 * not a display helper — kept here on the date-fns-free contract layer so both
 * the engine and the kind policies can reuse it without a dependency cycle.
 */
export function isActiveEntry(entry: CreditEntry, nowISO: string): boolean {
  return entry.expiresAt > nowISO && entry.grantedAt <= nowISO;
}

export interface UsageEntry {
  reachedPaywall: boolean;
  accountedCredits: number;
  usedCredits: number;
  featureUsage: Record<string, number>;
}

/**
 * Structure of user credits data: the flat active-credit ledger plus the
 * per-day consumption log. Crosses the DO wire (the `/state` RPC payload).
 */
export interface UserCreditsState {
  /** Flat list of active credit grants. */
  credits: ActiveCredits;
  /** Per-local-day usage counters, keyed `yyyy-LL-dd`. */
  usage: Record<string, UsageEntry>;
  /**
   * Credits set aside for runs billed on landing, holdId → expiry ISO (see
   * `RESERVE_CREDIT`). A live hold reads as spent everywhere a balance is shown
   * or checked; the ledger itself is untouched until the run settles.
   */
  holds?: Record<string, string>;
}

/** How many holds are still live at `nowISO`, leaving out `exceptHoldId`. */
export function liveHoldCount(
  holds: Record<string, string> | undefined,
  nowISO: string,
  exceptHoldId?: string,
): number {
  let n = 0;
  for (const [id, expiresAt] of Object.entries(holds ?? {})) {
    if (id !== exceptHoldId && expiresAt > nowISO) n++;
  }
  return n;
}

/** Per-attribution breakdown (available + used). */
export type CreditsBreakdown = Record<
  CreditsAttributionType,
  { available: number; used: number }
>;

/** Recursively freeze an object graph (defaults are shared, read-only constants). */
function deepFreeze<T>(obj: T): Readonly<T> {
  if (obj && typeof obj === 'object') {
    for (const value of Object.values(obj)) deepFreeze(value);
    Object.freeze(obj);
  }
  return obj;
}

/** Build a breakdown record with every attribution type pre-seeded. Derived from
 *  {@link CREDITS_ATTRIBUTION_TYPES} so new types need no hand-written leaves.
 *  A FRESH object every call — never share a mutable breakdown across summaries.
 *
 *  Lives here (not in `./view`) because the frozen {@link DEFAULT_CREDITS_SUMMARY}
 *  constant below is built from it; `./view` re-exports it for client display use. */
export function emptyCreditsBreakdown(available = 0): CreditsBreakdown {
  return Object.fromEntries(
    CREDITS_ATTRIBUTION_TYPES.map((t) => [t, { available, used: 0 }]),
  ) as CreditsBreakdown;
}

/**
 * Serialization-safe rollup of a user's credit position. This is the shape the
 * UserDo persists at `credits:summary` and syncs to clients as the read-only
 * `credits` entity — so it must survive a JSON round-trip.
 *
 * It carries NO `Infinity`: unlimited tiers set `unlimited:true` and
 * `available:0`, and clients branch on the flag rather than reading a sentinel
 * number (JSON has no `Infinity`, it would become `null`).
 */
export interface CreditsSummary {
  /** True for unlimited tiers (wizard/veteran/master). When set, `available`
   *  and `breakdown` are zeroed — read this flag, not the numbers. */
  unlimited: boolean;
  /** Total available (unexpired, unused) credits. `0` when `unlimited`. */
  available: number;
  /** Per-attribution available/used breakdown. All zero when `unlimited`. */
  breakdown: CreditsBreakdown;
  /** ISO of the soonest credit renewal, if any are scheduled. */
  nextRenewal?: string;
  /** Whether the user hit the paywall on `updatedAtISO`'s day. */
  paywallHitToday: boolean;
  /** ISO at which this summary was computed (the operation's `currentUTCDateISO`). */
  updatedAtISO: string;
  /** The tier's recurring allowance, if any (absent for unlimited/no-grant tiers). */
  periodicGrant?: PeriodicGrant;
}

const breakdownLeafSchema = z.object({
  available: z.number(),
  used: z.number(),
});

/** Schema for {@link PeriodicGrant} (`./kinds`) — the tier's recurring allowance. */
const periodicGrantSchema = z.object({
  attribution: z.enum(CREDITS_ATTRIBUTION_TYPES),
  amount: z.number(),
  period: z.enum(['day', 'month']),
});

/**
 * Base (strict) object schema for {@link CreditsSummary}. Kept separate from the
 * exported `.passthrough()` schema so the compile-time drift check below sees the
 * exact known-field shape WITHOUT passthrough's `[k: string]: unknown` index
 * signature (which would defeat a structural mutual-extends comparison).
 */
const creditsSummaryBaseSchema = z.object({
  unlimited: z.boolean(),
  available: z.number(),
  breakdown: z.object(
    Object.fromEntries(
      CREDITS_ATTRIBUTION_TYPES.map((t) => [t, breakdownLeafSchema]),
    ) as Record<CreditsAttributionType, typeof breakdownLeafSchema>,
  ),
  nextRenewal: z.string().optional(),
  paywallHitToday: z.boolean(),
  updatedAtISO: z.string(),
  periodicGrant: periodicGrantSchema.optional(),
});

/**
 * Compile-time guard that the zod schema and the hand-written TS type stay in
 * lockstep — replaces the old `as unknown as z.ZodType<CreditsSummary>` double
 * assertion (which silently disabled drift checking). If a field is added to one
 * side and not the other, one of these assignments stops compiling. `breakdown`
 * is normalized to a plain keyed record on both sides (the zod record widens its
 * value type), so the comparison is purely about the field set + scalar types.
 */
type SchemaSummary = z.infer<typeof creditsSummaryBaseSchema>;
type NormalizedSummary<T> = Omit<T, 'breakdown'> & {
  breakdown: Record<string, { available: number; used: number }>;
};
// Both directions (mutual-extends): schema ⊇ type AND type ⊇ schema.
const _summaryFwd: NormalizedSummary<SchemaSummary> =
  undefined as unknown as NormalizedSummary<CreditsSummary>;
const _summaryBack: NormalizedSummary<CreditsSummary> =
  undefined as unknown as NormalizedSummary<SchemaSummary>;
void _summaryFwd;
void _summaryBack;

/**
 * Zod schema for {@link CreditsSummary}, used to register the read-only `credits`
 * sync entity (inbound validation). `.passthrough()` for forward-compat (a newer
 * server may carry fields an older client doesn't know).
 */
export const creditsSummarySchema = creditsSummaryBaseSchema.passthrough();

/**
 * Default summary before the first server value arrives (client initial state).
 * Deep-frozen: it's a shared constant reused for every {@link CreditKind} in
 * {@link DEFAULT_CREDITS_SUMMARY_BY_KIND}, so a mutation would leak across kinds.
 * Callers that need a writable summary build their own via `summarizeCredits`.
 */
export const DEFAULT_CREDITS_SUMMARY: CreditsSummary = deepFreeze({
  unlimited: false,
  available: 0,
  breakdown: emptyCreditsBreakdown(),
  paywallHitToday: false,
  updatedAtISO: '',
}) as CreditsSummary;

/**
 * A user's full credit position across every {@link CreditKind} — one
 * {@link CreditsSummary} per wallet. This is the value the UserDo persists at
 * `credits:summary` and syncs as the read-only `credits` entity, so clients read
 * `summary.quiz` / `summary.transcription` from a single synced object.
 *
 * Keyed by `CreditKind`; adding a kind extends the map with no new sync entity.
 */
export type CreditsSummaryByKind = Record<CreditKind, CreditsSummary>;

/**
 * Zod schema for {@link CreditsSummaryByKind} — one summary per kind. Derived
 * from {@link CREDIT_KINDS} so a new kind needs no hand-written leaf.
 * `.passthrough()` for forward-compat (a newer server may carry extra kinds an
 * older client doesn't know).
 */
export const creditsSummaryByKindSchema = z
  .object(
    Object.fromEntries(CREDIT_KINDS.map((k) => [k, creditsSummarySchema])) as Record<
      CreditKind,
      typeof creditsSummarySchema
    >,
  )
  .passthrough();

/**
 * Default per-kind summary before the first server value arrives. Deep-frozen
 * (each leaf is the shared {@link DEFAULT_CREDITS_SUMMARY}); never mutate in place.
 */
export const DEFAULT_CREDITS_SUMMARY_BY_KIND: CreditsSummaryByKind = deepFreeze(
  Object.fromEntries(
    CREDIT_KINDS.map((k) => [k, DEFAULT_CREDITS_SUMMARY]),
  ) as CreditsSummaryByKind,
);

// ---------------------------------------------------------------------------
// DO wire-response schemas
//
// The `/internal/credits/*` RPCs return JSON the Hono bridge consumes. These
// schemas let the bridge `safeParse` each response and surface a clear error on
// contract drift instead of blind-casting `await res.json()`. Defined here next
// to the summary schema so the DO handlers and the server share one definition.
// `.passthrough()` for forward-compat (a newer DO may carry extra fields).
// ---------------------------------------------------------------------------

const creditEntrySchema = z
  .object({
    attributionType: z.enum(CREDITS_ATTRIBUTION_TYPES),
    granted: z.number(),
    used: z.number(),
    grantedAt: z.string(),
    expiresAt: z.string(),
    renewsAt: z.string().optional(),
    meta: z.record(z.string(), z.unknown()).optional(),
  })
  .passthrough();

const usageEntrySchema = z
  .object({
    reachedPaywall: z.boolean(),
    accountedCredits: z.number(),
    usedCredits: z.number(),
    featureUsage: z.record(z.string(), z.number()),
  })
  .passthrough();

/** Schema for {@link UserCreditsState} (the `state` RPC payload). */
export const userCreditsStateSchema = z
  .object({
    credits: z.array(creditEntrySchema),
    usage: z.record(z.string(), usageEntrySchema),
    holds: z.record(z.string(), z.string()).optional(),
  })
  .passthrough();

/**
 * `POST /internal/credits/apply` response — the persisted/replayed apply result.
 * `result` stays `unknown` (operation-specific; narrowed by the caller per op
 * type); `replayed` is set on an idempotent retry. `subscriptionStatus` is the
 * tier the DO billed against — present when the DO resolved it from its plan
 * mirror (a request that omitted the tier).
 */
export const creditsApplyWireSchema = z
  .object({
    modified: z.boolean(),
    result: z.unknown(),
    summary: creditsSummarySchema,
    replayed: z.boolean().optional(),
    subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES).optional(),
    /**
     * Question-budget outcome of a run-scoped consume. Recomputed
     * every request, replays included; never stored for replay.
     */
    runBudget: z
      .object({ exceeded: z.boolean(), remaining: z.number(), max: z.number() })
      .passthrough()
      .optional(),
  })
  .passthrough();

/** `POST /internal/credits/read` response. `subscriptionStatus` is the tier the
 *  DO resolved from its stored plan mirror and billed the summary against. */
export const creditsReadWireSchema = z
  .object({
    summary: creditsSummarySchema,
    subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES).optional(),
  })
  .passthrough();

/** `POST /internal/credits/tier` response: the user's tier as the DO knows it. */
export const creditsTierWireSchema = z
  .object({ subscriptionStatus: z.enum(SUBSCRIPTION_STATUSES) })
  .passthrough();

/** `POST /internal/credits/usage` response. */
export const creditsUsageWireSchema = z
  .object({ daysInARowReachedPaywall: z.number() })
  .passthrough();

/** `POST /internal/credits/state` response. */
export const creditsStateWireSchema = z
  .object({ state: userCreditsStateSchema })
  .passthrough();

/** Result payload of a REDUCE_CREDITS apply (the `result` field for that op). */
export const reduceCreditsResultSchema = z
  .object({
    hasUsedCredits: z.boolean(),
    hasEnoughCredits: z.boolean(),
    usedCredits: z.number(),
  })
  .passthrough();
